import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card } from './ui/card';
import { Loader2, AlertCircle, Eye, Filter, Plus, Minus, RotateCcw, ZoomIn, ZoomOut, Hash, Calendar, List, ChevronLeft, ChevronRight } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/auth-context';
import { supabase } from '@/integrations/supabase/client';
import MeasurementPopup from './MeasurementPopup';
import { getCurrentTenantSlug } from '@/utils/tenant-utils';
import { WorkspaceService } from '@/services/workspace-service';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

// Utility: parse sample date/time using format from metadata
function getSampleDateObj(sample: any) {
  const dateStr = sample.metadata?.date;
  const timeStr = sample.metadata?.time;
  const dateFormat = sample.metadata?.date_format || 'DD/MM/YYYY';
  const timeFormat = sample.metadata?.time_format || 'HH:mm:ss';
  
  if (dateStr && timeStr) {
    // Try parsing with the exact format first
    let result = dayjs(`${dateStr} ${timeStr}`, `${dateFormat} ${timeFormat}`);
    
    // If that fails, try with more flexible parsing
    if (!result.isValid()) {
      
      // Try different date formats that are more flexible
      const flexibleDateFormats = [
        'DD/MM/YYYY',
        'D/M/YYYY', 
        'DD/MM/YY',
        'D/M/YY',
        'MM/DD/YYYY',
        'M/D/YYYY',
        'MM/DD/YY',
        'M/D/YY'
      ];
      
      let dateResult = null;
      let timeResult = null;
      
      // Try to parse date with flexible formats
      for (const format of flexibleDateFormats) {
        dateResult = dayjs(dateStr, format);
        if (dateResult.isValid()) {
          break;
        }
      }
      
      // Try to parse time
      timeResult = dayjs(timeStr, timeFormat);
      if (!timeResult.isValid()) {
        // Try alternative time formats
        const timeFormats = ['HH:mm:ss', 'HH:mm', 'H:mm:ss', 'H:mm'];
        for (const format of timeFormats) {
          timeResult = dayjs(timeStr, format);
          if (timeResult.isValid()) {
            break;
          }
        }
      }
      
      if (dateResult && dateResult.isValid() && timeResult && timeResult.isValid()) {
        // Combine the valid date and time
        result = dateResult.hour(timeResult.hour()).minute(timeResult.minute()).second(timeResult.second());
      } else {
      }
    } else {
    }
    return result;
  } else if (dateStr) {
    const result = dayjs(dateStr, dateFormat);
    return result;
  } else if (sample.sample_date) {
    const result = dayjs(sample.sample_date);
    return result;
  } else {
    return dayjs('invalid'); // create an invalid date
  }
}

function getChronologicalSamples(samples: any[]) {
  return [...samples].sort((a, b) => {
    const dateA = getSampleDateObj(a);
    const dateB = getSampleDateObj(b);
    if (!dateA.isValid() && !dateB.isValid()) return 0;
    if (!dateA.isValid()) return 1;
    if (!dateB.isValid()) return -1;
    return dateB.valueOf() - dateA.valueOf(); // newest to oldest
  });
}

interface ManufacturingProcess {
  id: string;
  name: string;
  description?: string;
}

interface ManualConfig {
  id: string;
  form_title: string;
  data_type: string;
  config_data: {
    part_configuration_id?: string;
    parsing_configuration_id?: string;
  };
}

interface MarkerPosition {
  id: string;
  featureLabel: string;
  position: { x: number; y: number }; // 2D position for images
  page?: number; // Page number where this marker should be visible
}

interface MeasurementData {
  id: string;
  feature_label: string;
  characteristic: string;
  actual_value: number;
  nominal_value: number;
  upper_tolerance: number;
  lower_tolerance: number;
  sample_date: string;
  sample_id: string;
  metadata: any;
  deviation?: number; // Optional deviation field
}

interface ImageViewerModalProps {
  isOpen: boolean;
  onClose: () => void;
  station: ManufacturingProcess | null;
  formTitle: string;
  configId: string;
  manualConfigs: ManualConfig[];
}

const ImageViewerModal = ({ isOpen, onClose, station, formTitle, configId, manualConfigs }: ImageViewerModalProps) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [imageFiles, setImageFiles] = useState<any[]>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [markerPositions, setMarkerPositions] = useState<MarkerPosition[]>([]);
  const [measurementData, setMeasurementData] = useState<MeasurementData[]>([]);
  const [sampleData, setSampleData] = useState<any[]>([]);
  const [selectedSamples, setSelectedSamples] = useState<string[]>([]);
  const [showSampleSelection, setShowSampleSelection] = useState(false);
  const [partConfigData, setPartConfigData] = useState<any>(null);
  const [nominalData, setNominalData] = useState<any[]>([]);
  const [selectedMarkerId, setSelectedMarkerId] = useState<string | null>(null);
  const [selectedMarkerIds, setSelectedMarkerIds] = useState<string[]>([]);
  const [markerScreenPositions, setMarkerScreenPositions] = useState<{ [key: string]: { x: number; y: number } }>({});
  const [popupPosition, setPopupPosition] = useState({ x: 20, y: -650 });

  // Zoom and pan state
  const [scale, setScale] = useState(1);
  const [translateX, setTranslateX] = useState(0);
  const [translateY, setTranslateY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imageSize, setImageSize] = useState({ width: 0, height: 0 });
  const [imageDimensions, setImageDimensions] = useState({ width: 0, height: 0 });

  const [selectionMethod, setSelectionMethod] = useState<'count' | 'date' | 'manual'>('count');
  const [sampleCount, setSampleCount] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { user } = useAuth();

  // Get the config for this form
  const config = manualConfigs.find(c => c.id === configId);

  useEffect(() => {
    if (isOpen && config && user) {
      loadImageData();
    }
  }, [isOpen, config, user]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setImageUrl(null);
      setImageFiles([]);
      setCurrentPage(0);
      setMarkerPositions([]);
      setMeasurementData([]);
      setSampleData([]);
      setSelectedSamples([]);
      setShowSampleSelection(false);
      setPartConfigData(null);
      setNominalData([]);
      setSelectedMarkerId(null);
      setError(null);
      setScale(1);
      setTranslateX(0);
      setTranslateY(0);
    }
  }, [isOpen]);

  // Reset current page when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentPage(0);
    }
  }, [isOpen]);

  // Clear all measurement popups when page changes
  useEffect(() => {
    setSelectedMarkerIds([]);
  }, [currentPage]);

  // Initialize markers if not already set
  useEffect(() => {
    if (partConfigData && partConfigData.marker_positions && Array.isArray(partConfigData.marker_positions)) {
      const markers: MarkerPosition[] = partConfigData.marker_positions
        .filter((marker: any) => {
          // Only include markers that are actually positioned (non-zero coordinates)
          const hasPosition = marker.x !== 0 || marker.y !== 0;
          return hasPosition;
        })
        .map((marker: any, index: number) => ({
          id: marker.featureLabel || `marker_${index}`,
          featureLabel: marker.featureLabel || `Feature_${index}`,
          position: { x: marker.x || 0, y: marker.y || 0 },
          page: marker.page || 0 // Default to page 0 if not specified
        }));
      setMarkerPositions(markers);
    }
  }, [partConfigData]);

  // Process measurement data when selected samples change
  useEffect(() => {
    if (sampleData.length > 0 && selectedSamples.length > 0) {
      processMeasurementData();
    }
  }, [sampleData, selectedSamples]);

  // Update current image when page changes
  useEffect(() => {
    if (imageFiles.length > 0 && currentPage < imageFiles.length) {
      setImageUrl(imageFiles[currentPage].url);
      // Reset zoom and pan when changing pages
      setScale(1);
      setTranslateX(0);
      setTranslateY(0);
    }
  }, [currentPage, imageFiles]);

  const loadImageData = async () => {
    // Validate workspace access with fallback logic (same as STL viewer)
    const currentSlug = getCurrentTenantSlug();
    let workspace = currentSlug ? await WorkspaceService.getWorkspaceBySlug(currentSlug) : null;
    
    // If workspace not found by slug, try fallback logic
    if (!workspace) {
      try {
        const userWorkspaces = await WorkspaceService.getUserWorkspaces();
        if (userWorkspaces && userWorkspaces.length > 0) {
          workspace = userWorkspaces.find(ws => ws.is_default) || userWorkspaces[0];
        }
      } catch (error) {
        console.error('Error getting user workspaces for image loading:', error);
      }
    }
    
    if (!workspace) {
      setError('No workspace found or access denied');
      setLoading(false);
      return;
    }

    if (!config?.config_data?.part_configuration_id) {
      setError('No part configuration found for this data collection');
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 1. Load part configuration (image file path and marker positions)
      const { data: partConfig, error: partError } = await (supabase as any)
        .from('part_configurations')
        .select('*')
        .eq('id', config.config_data.part_configuration_id)
        .single();

      if (partError) throw partError;

      if (!partConfig) {
        throw new Error('Part configuration not found');
      }

      setPartConfigData(partConfig);

      // 2. Get image files from storage
      if (partConfig.image_files && Array.isArray(partConfig.image_files) && partConfig.image_files.length > 0) {
        // Load multiple images from new format
        const imagePromises = partConfig.image_files.map(async (imageFile: any, index: number) => {
          
          if (!imageFile.path) {
            console.warn(`⚠️ Image file ${index} missing path:`, imageFile);
            return null;
          }
          
          const { data: urlData, error: urlError } = await supabase.storage
            .from('image-files')
            .createSignedUrl(imageFile.path, 3600); // 1 hour expiry
          
          if (urlError) {
            console.error(`❌ Error getting signed URL for ${imageFile.path}:`, urlError);
            return null;
          }
          
          return {
            ...imageFile,
            url: urlData?.signedUrl || null
          };
        });
        
        const imageResults = await Promise.all(imagePromises);
        const validImages = imageResults.filter(img => img && img.url);
        
        if (validImages.length > 0) {
          setImageFiles(validImages);
          setImageUrl(validImages[0].url); // Set first image as current
          setCurrentPage(0);
        } else {
          throw new Error('Failed to get image file URLs - no valid images found');
        }
      } else if (partConfig.image_file_path) {
        // Fallback: Parse old image_file_path format
        
        try {
          let imagePaths: string[] = [];
          
          // Try to parse as JSON array
          if (partConfig.image_file_path.startsWith('[') && partConfig.image_file_path.endsWith(']')) {
            imagePaths = JSON.parse(partConfig.image_file_path);
          } else {
            // Single string path
            imagePaths = [partConfig.image_file_path];
          }
          
          if (imagePaths.length > 0) {
            // Load images from parsed paths
            const imagePromises = imagePaths.map(async (path: string, index: number) => {
              
              const { data: urlData, error: urlError } = await supabase.storage
                .from('image-files')
                .createSignedUrl(path, 3600); // 1 hour expiry
              
              if (urlError) {
                console.error(`❌ Error getting signed URL for ${path}:`, urlError);
                return null;
              }
              
              return {
                path: path,
                name: `Page ${index + 1}`,
                order: index + 1,
                url: urlData?.signedUrl || null
              };
            });
            
            const imageResults = await Promise.all(imagePromises);
            const validImages = imageResults.filter(img => img && img.url);
            
            if (validImages.length > 0) {
              setImageFiles(validImages);
              setImageUrl(validImages[0].url); // Set first image as current
              setCurrentPage(0);
            } else {
              throw new Error('Failed to get image file URLs from old format - no valid images found');
            }
          } else {
            throw new Error('No image paths found in old format');
          }
        } catch (parseError) {
          console.error('❌ Error parsing old image_file_path format:', parseError);
          throw new Error('Failed to parse old image file format');
        }
      } else {
        console.error('❌ No valid image files found in part configuration:', {
          hasImageFiles: !!partConfig.image_files,
          isArray: Array.isArray(partConfig.image_files),
          length: partConfig.image_files?.length,
          imageFiles: partConfig.image_files,
          allKeys: Object.keys(partConfig)
        });
        
        // Try to list files in storage to see if there are any images for this part
        try {
          const { data: storageFiles, error: listError } = await supabase.storage
            .from('image-files')
            .list('', {
              limit: 100,
              offset: 0,
              sortBy: { column: 'name', order: 'asc' }
            });
          
          console.log('🔍 Storage files found:', {
            count: storageFiles?.length || 0,
            files: storageFiles?.map(f => f.name),
            error: listError
          });
          
          // Look for files that might belong to this part configuration
          const partId = partConfig.id;
          const relevantFiles = storageFiles?.filter(f => 
            f.name.includes(partId) || f.name.includes('Blah') // Look for files related to this part
          ) || [];
          
          console.log('🔍 Relevant files for this part:', relevantFiles);
          
          if (relevantFiles.length > 0) {
            throw new Error(`Found ${relevantFiles.length} image files in storage but they are not configured in the part configuration. Please update the configuration to include these images.`);
          }
        } catch (storageError) {
          console.error('❌ Error checking storage:', storageError);
        }
        
        // Check if this might be a migrated configuration that lost its images
        if (partConfig.image_files && Array.isArray(partConfig.image_files) && partConfig.image_files.length === 0) {
          throw new Error('This part configuration has no images configured. Please add images to the configuration first.');
        } else {
          throw new Error('No image files configured for this part');
        }
      }

      // 3. Extract marker positions from part configuration
      if (partConfig.marker_positions && Array.isArray(partConfig.marker_positions)) {
        
        const markers: MarkerPosition[] = partConfig.marker_positions
          .filter((marker: any) => {
            // Only include markers that are actually positioned (non-zero coordinates)
            const hasPosition = marker.x !== 0 || marker.y !== 0;
            return hasPosition;
          })
          .map((marker: any, index: number) => ({
            id: marker.featureLabel || `marker_${index}`,
            featureLabel: marker.featureLabel || `Feature_${index}`,
            position: { x: marker.x || 0, y: marker.y || 0 },
            page: marker.page || 0 // Default to page 0 if not specified
          }));
        
        setMarkerPositions(markers);
      }

      // 4. Load measurement data from sample_data table
      const { data: sampleDataResult, error: sampleError } = await (supabase as any)
        .from('sample_data')
        .select('*')
        .eq('part_configuration_id', config.config_data.part_configuration_id)
        .order('sample_date', { ascending: false });

      if (sampleError) {
        console.error('Error loading sample data:', sampleError);
      } else {
        setSampleData(sampleDataResult || []);
        // Initially select all samples
        const allSampleIds = (sampleDataResult || []).map((sample: any) => 
          sample.metadata?.sample_id || sample.id || `Sample ${sample.sample_date}`
        );
        setSelectedSamples(allSampleIds);
      }

      // 5. Load nominal information from nominal_information table
      const { data: nominalInfo, error: nominalError } = await (supabase as any)
        .from('nominal_information')
        .select('*')
        .eq('part_configuration_id', config.config_data.part_configuration_id);

      if (nominalError) {
        console.error('Error loading nominal information:', nominalError);
      } else {
        setNominalData(nominalInfo || []);
      }

    } catch (error) {
      console.error('Error loading image data:', error);
      setError(error instanceof Error ? error.message : 'Failed to load 2D image data');
      toast({
        title: "Error",
        description: "Failed to load 2D image data",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const getCurrentPageMarkers = () => {
    // Filter markers for current page and only show positioned markers
    const currentPageMarkers = markerPositions.filter(marker => 
      marker.page === currentPage && 
      (marker.position.x !== 0 || marker.position.y !== 0)
    );
    
    return currentPageMarkers;
  };

  const processMeasurementData = () => {
    // Process measurement data from selected samples
    const allMeasurements: MeasurementData[] = [];
    
    selectedSamples.forEach(sampleId => {
      const sample = sampleData.find(s => 
        (s.metadata?.sample_id || s.id || `Sample ${s.sample_date}`) === sampleId
      );
      
      if (sample) {
        // Check if measurements is an array (same as STL viewer)
        if (sample.measurements && Array.isArray(sample.measurements)) {
          
          sample.measurements.forEach((measurement: any, index: number) => {
            allMeasurements.push({
              id: `${sample.id}_${measurement.feature_label || index}`,
              feature_label: measurement.feature_label || `Feature_${index}`,
              characteristic: measurement.characteristic || 'Dimension',
              actual_value: parseFloat(measurement.actual_value || 0),
              nominal_value: parseFloat(measurement.nominal_value || measurement.nominal || 0),
              upper_tolerance: parseFloat(measurement.upper_tolerance || 0),
              lower_tolerance: parseFloat(measurement.lower_tolerance || 0),
              sample_date: sample.sample_date,
              sample_id: sample.metadata?.sample_id || sample.id || `Sample ${sample.sample_date}`,
              metadata: sample.metadata || {},
              deviation: measurement.deviation // Add deviation
            });
          });
        } else {
        }
      } else {
      }
    });
    
    setMeasurementData(allMeasurements);
  };

  const handleImageLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
    const img = event.currentTarget;
    setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    
    // Auto-fit the image to container on load
    if (containerRef.current) {
      const container = containerRef.current;
      const containerWidth = container.clientWidth - 40; // Padding
      const containerHeight = container.clientHeight - 40;
      
      const scaleX = containerWidth / img.naturalWidth;
      const scaleY = containerHeight / img.naturalHeight;
      const fitScale = Math.min(scaleX, scaleY, 1); // Don't scale up beyond 100%
      
      setScale(fitScale);
      setTranslateX((containerWidth - img.naturalWidth * fitScale) / 2);
      setTranslateY((containerHeight - img.naturalHeight * fitScale) / 2);
    }
    
    setImageSize({ width: img.offsetWidth, height: img.offsetHeight });
  };

  const handleMarkerClick = (markerId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    
    // Add marker to selected markers array (like STLViewerModal)
    setSelectedMarkerIds(prev => {
      if (prev.includes(markerId)) {
        // If already selected, remove it
        return prev.filter(id => id !== markerId);
      } else {
        // If not selected, add it
        return [...prev, markerId];
      }
    });
    
    // Position popup near the click
    const rect = event.currentTarget.getBoundingClientRect();
    const containerRect = containerRef.current?.getBoundingClientRect();
    
    if (containerRect) {
      // Calculate marker screen position relative to container
      const markerScreenX = event.clientX - containerRect.left;
      const markerScreenY = event.clientY - containerRect.top;
      
      // Store marker screen position for line drawing (use raw screen coordinates)
      setMarkerScreenPositions(prev => ({
        ...prev,
        [markerId]: { x: event.clientX, y: event.clientY }
      }));
      
      // Calculate popup position with bounds checking
      const popupWidth = 320; // w-80 = 320px
      const popupHeight = 384; // max-h-96 = 384px
      const margin = 20;
      
      let popupX = markerScreenX + 20;
      let popupY = markerScreenY - 50;
      
      // Ensure popup stays within container bounds
      const containerWidth = containerRect.width;
      const containerHeight = containerRect.height;
      
      // Adjust X position if popup would go off-screen
      if (popupX + popupWidth > containerWidth - margin) {
        popupX = markerScreenX - popupWidth - 20; // Position to the left of marker
      }
      if (popupX < margin) {
        popupX = margin;
      }
      
      // Adjust Y position if popup would go off-screen
      if (popupY + popupHeight > containerHeight - margin) {
        popupY = containerHeight - popupHeight - margin;
      }
      if (popupY < margin) {
        popupY = margin;
      }
      
      // Position popup higher up on the canvas (same as STL viewer)
      setPopupPosition({ 
        x: 20, 
        y: -650 
      });
    }
  };

  const handleClosePopup = (markerId?: string) => {
    if (markerId) {
      // Remove specific marker from selection
      setSelectedMarkerIds(prev => prev.filter(id => id !== markerId));
    } else {
      // Clear all selections
      setSelectedMarkerIds([]);
    }
  };

  const getMarkerMeasurements = (markerId: string) => {
    const marker = markerPositions.find(m => m.id === markerId);
    if (!marker) return [];
    
    const measurements = measurementData.filter(m => 
      m.feature_label === marker.featureLabel || 
      m.feature_label === markerId
    );
    
    return measurements;
  };

  // Utility: calculate tolerance compliance percentage for a marker
  const getMarkerToleranceCompliance = (markerId: string) => {
    const marker = markerPositions.find(m => m.id === markerId);
    if (!marker) return { percentage: 0, color: 'bg-gray-500' };

    // Get all measurements for this marker
    const markerMeasurements = measurementData.filter(m => 
      m.feature_label === marker.featureLabel
    );

    if (markerMeasurements.length === 0) {
      return { percentage: 0, color: 'bg-gray-500' }; // No data
    }

    // Get all nominal data for this marker (across all characteristics)
    const nominalInfos = nominalData.filter(n => 
      n.feature_label === marker.featureLabel
    );

    if (nominalInfos.length === 0) {
      return { percentage: 0, color: 'bg-gray-500' }; // No tolerance data
    }

    // Calculate tolerance compliance across all characteristics
    let totalMeasurements = 0;
    let totalInTolerance = 0;

    nominalInfos.forEach(nominalInfo => {
      if (nominalInfo.upper_tolerance === undefined || nominalInfo.lower_tolerance === undefined) {
        return; // Skip characteristics without tolerance data
      }

      // Get measurements for this specific characteristic
      const characteristicMeasurements = markerMeasurements.filter(m => 
        m.characteristic === nominalInfo.characteristic
      );

      characteristicMeasurements.forEach(measurement => {
        totalMeasurements++;
        
        const deviation = measurement.deviation !== undefined 
          ? measurement.deviation 
          : measurement.actual_value - (nominalInfo.nominal || 0);
        
        if (deviation >= nominalInfo.lower_tolerance && deviation <= nominalInfo.upper_tolerance) {
          totalInTolerance++;
        }
      });
    });

    if (totalMeasurements === 0) {
      return { percentage: 0, color: 'bg-gray-500' }; // No valid measurements
    }

    const percentage = (totalInTolerance / totalMeasurements) * 100;

    // Determine color based on compliance percentage
    let color = 'bg-gray-500'; // Default
    if (percentage >= 95) {
      color = 'bg-green-500'; // Above 95% - Green
    } else if (percentage >= 90) {
      color = 'bg-yellow-500'; // 90-95% - Yellow
    } else if (percentage >= 80) {
      color = 'bg-orange-500'; // 80-90% - Orange
    } else {
      color = 'bg-red-500'; // Less than 80% - Red
    }

    return { percentage, color };
  };

  // Zoom controls
  const handleZoomIn = () => {
    setScale(prev => Math.min(prev * 1.2, 5));
  };

  const handleZoomOut = () => {
    setScale(prev => Math.max(prev / 1.2, 0.1));
  };

  const handleFitToView = () => {
    if (containerRef.current && imageDimensions.width > 0) {
      const container = containerRef.current;
      const containerWidth = container.clientWidth - 40;
      const containerHeight = container.clientHeight - 40;
      
      const scaleX = containerWidth / imageDimensions.width;
      const scaleY = containerHeight / imageDimensions.height;
      const fitScale = Math.min(scaleX, scaleY, 1);
      
      setScale(fitScale);
      setTranslateX((containerWidth - imageDimensions.width * fitScale) / 2);
      setTranslateY((containerHeight - imageDimensions.height * fitScale) / 2);
    }
  };

  // Pan controls
  const handleMouseDown = (event: React.MouseEvent) => {
    if (event.shiftKey) {
      setIsDragging(true);
      setDragStart({ x: event.clientX - translateX, y: event.clientY - translateY });
    }
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (isDragging) {
      setTranslateX(event.clientX - dragStart.x);
      setTranslateY(event.clientY - dragStart.y);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Wheel zoom
  const handleWheel = (event: React.WheelEvent) => {
    event.preventDefault();
    const zoomFactor = event.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.min(Math.max(scale * zoomFactor, 0.1), 5);
    
    // Zoom towards mouse cursor
    const rect = event.currentTarget.getBoundingClientRect();
    const mouseX = event.clientX - rect.left;
    const mouseY = event.clientY - rect.top;
    
    const scaleDiff = newScale - scale;
    const newTranslateX = translateX - (mouseX - translateX) * (scaleDiff / scale);
    const newTranslateY = translateY - (mouseY - translateY) * (scaleDiff / scale);
    
    setScale(newScale);
    setTranslateX(newTranslateX);
    setTranslateY(newTranslateY);
  };

  // Helper functions for sample selection
  const validateSampleCount = (count: string): boolean => {
    const num = parseInt(count, 10);
    return !isNaN(num) && num > 0 && num <= sampleData.length;
  };

  const getFilteredSamples = () => {
    if (selectionMethod === 'count') {
      if (!validateSampleCount(sampleCount)) return [];
      const count = parseInt(sampleCount, 10);
      // Get newest to oldest, then select the newest X samples (from the beginning)
      const sortedSamples = getChronologicalSamples(sampleData);
      return sortedSamples.slice(0, count); // first X samples (newest)
    } else if (selectionMethod === 'date') {
      if (!dateFrom || !dateTo) return [];
      const fromDate = new Date(dateFrom);
      const toDate = new Date(dateTo);
      // Filter by date range and sort oldest to newest
      const filtered = getChronologicalSamples(sampleData).filter(sample => {
        const sampleDate = getSampleDateObj(sample);
        return sampleDate.isValid() && sampleDate.isAfter(dayjs(fromDate).subtract(1, 'day')) && sampleDate.isBefore(dayjs(toDate).add(1, 'day'));
      });
      return filtered;
    } else {
      // Manual selection - filter selected samples and sort oldest to newest
      const filtered = getChronologicalSamples(sampleData).filter(sample => {
        const sampleId = sample.metadata?.sample_id || sample.id || `Sample ${sample.sample_date}`;
        return selectedSamples.includes(sampleId);
      });
      return filtered;
    }
  };

  const applySelection = () => {
    const filtered = getFilteredSamples();
    const filteredIds = filtered.map(sample => 
      sample.metadata?.sample_id || sample.id || `Sample ${sample.sample_date}`
    );
    setSelectedSamples(filteredIds);
    setShowSampleSelection(false);
    processMeasurementData();
  };

  const handleSampleToggle = (sampleId: string) => {
    setSelectedSamples(prev => 
      prev.includes(sampleId) 
        ? prev.filter(id => id !== sampleId)
        : [...prev, sampleId]
    );
  };

  const selectAllSamples = () => {
    const allIds = sampleData.map(sample => 
      sample.metadata?.sample_id || sample.id || `Sample ${sample.sample_date}`
    );
    setSelectedSamples(allIds);
  };

  const deselectAllSamples = () => {
    setSelectedSamples([]);
  };

  const handleClose = () => {
    onClose();
  };

  const handleEscapeKey = () => {
    // If there are open measurement popups, close them instead of closing the modal
    if (selectedMarkerIds.length > 0) {
      setSelectedMarkerIds([]);
      return;
    }
    
    // If no popup is open, close the modal
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleEscapeKey}>
      <DialogContent className="max-w-[calc(100vw-20px)] max-h-[calc(100vh-20px)] overflow-hidden p-3">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Eye className="w-5 h-5" />
            2D Image Viewer - {formTitle}
          </DialogTitle>
          <DialogDescription>
            Interactive 2D visualization for {station?.name}
          </DialogDescription>
          {partConfigData && (
            <div className="flex items-center gap-2 mt-2">
              <Badge variant="outline">{partConfigData.part_number}</Badge>
              <Badge variant="secondary">{markerPositions.length} markers</Badge>
              <Badge variant="secondary">{sampleData.length} samples</Badge>
              {sampleData.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={() => setShowSampleSelection(true)}
                  className="ml-2"
                >
                  <Filter className="w-4 h-4 mr-1" />
                  Select Samples ({selectedSamples.length})
                </Button>
              )}
            </div>
          )}
        </DialogHeader>
        
        <div className="flex-1 h-[calc(100vh-200px)] relative overflow-hidden" id="image-viewer-container">
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-10">
              <div className="text-center">
                <Loader2 className="w-8 h-8 animate-spin mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Loading 2D image...</p>
              </div>
            </div>
          )}

          {error && (
            <div className="absolute inset-0 flex items-center justify-center bg-background/80 backdrop-blur-sm z-10">
              <div className="text-center max-w-md">
                <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Failed to Load 2D Image</h3>
                <p className="text-sm text-muted-foreground mb-4">{error}</p>
                <div className="space-y-2">
                  <Button onClick={loadImageData} variant="outline" size="sm">
                    Try Again
                  </Button>
                  <Button onClick={handleClose} variant="ghost" size="sm">
                    Close
                  </Button>
                </div>
              </div>
            </div>
          )}

          {imageUrl && !loading && !error && (
            <div className="relative w-full h-full">
              {/* Control Panel */}
              <div className="absolute top-4 right-4 z-20 bg-background/90 backdrop-blur-sm border rounded-lg p-2">
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleZoomOut}
                    title="Zoom Out"
                  >
                    <Minus className="w-4 h-4" />
                  </Button>
                  <span className="text-xs text-muted-foreground min-w-[3rem] text-center">
                    {Math.round(scale * 100)}%
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleZoomIn}
                    title="Zoom In"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleFitToView}
                    title="Fit to View"
                  >
                    <RotateCcw className="w-4 h-4" />
                  </Button>
                </div>
                
                {/* Page Navigation */}
                {imageFiles.length > 1 && (
                  <div className="flex items-center gap-1 mt-2 pt-2 border-t">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.max(0, prev - 1))}
                      disabled={currentPage === 0}
                      title="Previous Page"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-xs text-muted-foreground min-w-[4rem] text-center">
                      {currentPage + 1} / {imageFiles.length}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setCurrentPage(prev => Math.min(imageFiles.length - 1, prev + 1))}
                      disabled={currentPage === imageFiles.length - 1}
                      title="Next Page"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Instructions Panel */}
              <div className="absolute top-4 left-4 z-20 bg-background/90 backdrop-blur-sm border rounded-lg p-2">
                <div className="text-xs text-muted-foreground space-y-1">
                  <div>• Scroll to zoom</div>
                  <div>• Shift+drag to pan</div>
                  <div>• Click markers for data</div>
                </div>
              </div>

              {/* Image Container */}
              <div 
                ref={containerRef}
                className="w-full h-full overflow-hidden cursor-crosshair"
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onWheel={handleWheel}
                style={{ cursor: isDragging ? 'grabbing' : 'default' }}
              >
                <div
                  style={{
                    transform: `translate(${translateX}px, ${translateY}px) scale(${scale})`,
                    transformOrigin: '0 0',
                    transition: isDragging ? 'none' : 'transform 0.1s ease-out'
                  }}
                  className="relative"
                >
                  <img
                    ref={imageRef}
                    src={imageUrl}
                    alt="Part visualization"
                    onLoad={handleImageLoad}
                    className="max-w-none"
                    draggable={false}
                  />
                  
                  {/* Markers */}
                  {getCurrentPageMarkers().map((marker) => {
                    const { percentage, color } = getMarkerToleranceCompliance(marker.id);
                    const markerMeasurements = getMarkerMeasurements(marker.id);
                    
                    // Convert Tailwind color classes to CSS colors
                    const getColorValue = (colorClass: string) => {
                      switch (colorClass) {
                        case 'bg-green-500': return '#10b981';
                        case 'bg-yellow-500': return '#eab308';
                        case 'bg-orange-500': return '#f97316';
                        case 'bg-red-500': return '#ef4444';
                        case 'bg-blue-500': return '#3b82f6';
                        default: return '#6b7280'; // gray-500
                      }
                    };
                    
                    const markerColor = getColorValue(color);
                    
                    return (
                      <div
                        key={marker.id}
                        className={`absolute cursor-pointer transform -translate-x-1/2 -translate-y-1/2 z-10 ${
                          selectedMarkerIds.includes(marker.id) 
                            ? 'w-6 h-6 border-2 border-white shadow-lg' 
                            : 'w-4 h-4 rounded-full flex items-center justify-center hover:scale-110 transition-transform'
                        }`}
                        style={{
                          left: marker.position.x,
                          top: marker.position.y,
                          backgroundColor: markerColor,
                          border: selectedMarkerIds.includes(marker.id) ? '2px solid white' : '1px solid white',
                          boxShadow: selectedMarkerIds.includes(marker.id) 
                            ? `0 0 10px 3px ${markerColor}, 0 0 20px 6px ${markerColor}40` 
                            : 'none'
                        }}
                        onClick={(e) => handleMarkerClick(marker.id, e)}
                        title={`${marker.featureLabel} - ${percentage.toFixed(1)}% in tolerance (${markerMeasurements.length} measurements across all characteristics)`}
                      >
                        {/* Marker label */}
                        <div className="absolute top-6 left-1/2 transform -translate-x-1/2 bg-black/80 text-white text-xs px-1 py-0.5 rounded whitespace-nowrap pointer-events-none">
                          {marker.featureLabel}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {!imageUrl && !loading && !error && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center">
                <Eye className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">No 2D Image Available</h3>
                <p className="text-sm text-muted-foreground">
                  This configuration doesn't have a 2D image set up yet.
                </p>
              </div>
            </div>
          )}

          {/* Measurement Popups */}
          {selectedMarkerIds.length > 0 && imageUrl && !loading && !error && (
            <>
              {selectedMarkerIds.map(markerId => {
                const { percentage, color } = getMarkerToleranceCompliance(markerId);
                return (
                  <MeasurementPopup
                    key={markerId}
                    markerId={markerId}
                    featureLabel={markerPositions.find(m => m.id === markerId)?.featureLabel || markerId}
                    measurements={getMarkerMeasurements(markerId)}
                    nominalData={nominalData}
                    onClose={() => handleClosePopup(markerId)}
                    position={popupPosition}
                    markerScreenPosition={markerScreenPositions[markerId]}
                    containerId="image-viewer-container"
                  />
                );
              })}
            </>
          )}
        </div>
      </DialogContent>

      {/* Enhanced Sample Selection Modal (copied from STLViewerModal) */}
      <Dialog open={showSampleSelection} onOpenChange={setShowSampleSelection}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Filter className="w-5 h-5" />
              Select Samples to Display
            </DialogTitle>
            <DialogDescription>
              Choose which samples to include in the 2D visualization and analysis
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto">
            <Tabs value={selectionMethod} onValueChange={(value) => setSelectionMethod(value as 'count' | 'date' | 'manual')}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="count" className="flex items-center gap-2">
                  <Hash className="h-4 w-4" />
                  By Count
                </TabsTrigger>
                <TabsTrigger value="date" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  By Date Range
                </TabsTrigger>
                <TabsTrigger value="manual" className="flex items-center gap-2">
                  <List className="h-4 w-4" />
                  Manual Selection
                </TabsTrigger>
              </TabsList>
              <TabsContent value="count" className="space-y-4 mt-6">
                <div className="space-y-2">
                  <Label htmlFor="sample-count">Number of samples (latest X samples)</Label>
                  <Input
                    id="sample-count"
                    type="number"
                    placeholder={`Enter number (1-${sampleData.length})`}
                    value={sampleCount}
                    onChange={(e) => setSampleCount(e.target.value)}
                    min="1"
                    max={sampleData.length}
                  />
                  {sampleCount && !validateSampleCount(sampleCount) && (
                    <p className="text-sm text-red-600">
                      Please enter a valid number between 1 and {sampleData.length}
                    </p>
                  )}
                  {sampleCount && validateSampleCount(sampleCount) && (
                    <p className="text-sm text-green-600">
                      Will select the latest {sampleCount} samples
                    </p>
                  )}
                </div>
              </TabsContent>
              <TabsContent value="date" className="space-y-4 mt-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date-from">Date from:</Label>
                    <Input
                      id="date-from"
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="date-to">Date to:</Label>
                    <Input
                      id="date-to"
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                    />
                  </div>
                </div>
                {dateFrom && dateTo && (
                  <p className="text-sm text-green-600">
                    Will select {getFilteredSamples().length} samples from {new Date(dateFrom).toLocaleDateString()} to {new Date(dateTo).toLocaleDateString()}
                  </p>
                )}
              </TabsContent>
              <TabsContent value="manual" className="space-y-4 mt-6">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    Select individual samples ({selectedSamples.length} of {sampleData.length} selected) - sorted by date/time (oldest first)
                  </span>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={selectAllSamples}>
                      Select All
                    </Button>
                    <Button variant="outline" size="sm" onClick={deselectAllSamples}>
                      Clear All
                    </Button>
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-2 max-h-60 overflow-y-auto border rounded p-3">
                  {getChronologicalSamples(sampleData).map((sample, index) => {
                    const sampleId = sample.metadata?.sample_id || sample.id || `Sample ${sample.sample_date}`;
                    const isSelected = selectedSamples.includes(sampleId);
                    const dateObj = getSampleDateObj(sample);
                    const displayDate = dateObj.isValid() ? dateObj.format('YYYY-MM-DD HH:mm:ss') : 'Invalid date';
                    const measurementCount = sample.measurements ? sample.measurements.length : 0;
                    
                    // Debug logging for invalid dates
                    if (!dateObj.isValid()) {
                      console.log('❌ Invalid date for sample:', {
                        sampleId,
                        metadata: sample.metadata,
                        sample_date: sample.sample_date,
                        dateObj: dateObj.format()
                      });
                    }
                    
                    return (
                      <div key={index} className="flex items-center justify-between p-2 hover:bg-gray-100 rounded">
                        <div className="flex items-center space-x-3">
                          <Checkbox
                            id={`sample-${index}`}
                            checked={isSelected}
                            onCheckedChange={() => handleSampleToggle(sampleId)}
                          />
                          <div className="flex-1">
                            <div className="font-medium text-sm">{sampleId}</div>
                            <div className="text-xs text-gray-500">{displayDate}</div>
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Badge variant="outline" className="text-xs">
                            {measurementCount} measurements
                          </Badge>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </TabsContent>
            </Tabs>
          </div>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button 
              variant="outline" 
              onClick={() => setShowSampleSelection(false)}
            >
              Cancel
            </Button>
            <Button 
              onClick={applySelection}
              disabled={
                (selectionMethod === 'count' && !validateSampleCount(sampleCount)) ||
                (selectionMethod === 'date' && (!dateFrom || !dateTo)) ||
                (selectionMethod === 'manual' && selectedSamples.length === 0)
              }
            >
              Apply Selection
              {selectionMethod === 'count' && validateSampleCount(sampleCount) && ` (${sampleCount} samples)`}
              {selectionMethod === 'date' && dateFrom && dateTo && ` (${getFilteredSamples().length} samples)`}
              {selectionMethod === 'manual' && ` (${selectedSamples.length} samples)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default ImageViewerModal; 